import { defineStore } from 'pinia';
import { computed, markRaw, reactive, ref, watch } from 'vue';
import { createEventHook } from '@vueuse/core';
import type { Maybe } from '@/src/types';
import type { Layout, LayoutItem } from '@/src/types/layout';
import { useIdStore } from '@/src/store/id';
import type { ViewInfo, ViewInfoInit, ViewType } from '@/src/types/views';
import { DefaultNamedLayouts, getAvailableViews } from '@/src/config';
import {
  parseNamedLayouts,
  type LayoutConfig,
} from '@/src/utils/layoutParsing';
import type { Manifest, StateFile } from '../io/state-file/schema';
import { onImageDeleted } from '@/src/composables/onImageDeleted';
import { declareManifestRefs } from '@/src/core/manifestRefs';
import { isRecord } from '@/src/utils';

// The manifest references this store's remove cascade keeps clean (see the
// onImageDeleted registration below), declared for the dev-only save backstop.
declareManifestRefs('views', (manifest) => {
  const views = isRecord(manifest.viewByID) ? manifest.viewByID : {};
  return [
    ...Object.entries(views).flatMap(([id, raw]) =>
      isRecord(raw) && typeof raw.dataID === 'string'
        ? [
            {
              kind: 'dataset' as const,
              id: raw.dataID,
              where: `viewByID[${id}].dataID`,
            },
          ]
        : []
    ),
    ...(typeof manifest.activeView === 'string'
      ? [
          {
            kind: 'view' as const,
            id: manifest.activeView,
            where: 'activeView',
          },
        ]
      : []),
    ...(Array.isArray(manifest.layoutSlots)
      ? manifest.layoutSlots
      : []
    ).flatMap((id) =>
      typeof id === 'string'
        ? [{ kind: 'view' as const, id, where: 'layoutSlots' }]
        : []
    ),
  ];
});

const DEFAULT_VIEW_INIT: ViewInfoInit = {
  type: '2D',
  dataID: null,
  name: 'Axial',
  options: {
    orientation: 'Axial',
  },
};

function iterLayout(
  layout: Layout,
  cb: (item: LayoutItem & { type: 'slot' }) => void
) {
  layout.items.forEach((item) => {
    if (item.type === 'slot') cb(item);
    else iterLayout(item, cb);
  });
}

function calcLayoutViewCount(layout: Layout): number {
  return layout.items.reduce((sum, item) => {
    if (item.type === 'slot') return sum + 1;
    return sum + calcLayoutViewCount(item);
  }, 0);
}

function generateLayoutFromGrid(size: [number, number]): Layout {
  const [width, height] = size;
  return {
    direction: 'column',
    items: Array.from({ length: height }).map((_, rowIndex) => {
      return {
        type: 'layout',
        direction: 'row',
        items: Array.from({ length: width }).map((__, colIndex) => {
          return {
            type: 'slot',
            slotIndex: rowIndex * width + colIndex,
          };
        }),
      };
    }),
  };
}

function needsViewReplacement(existingView: ViewInfo, nextView: ViewInfoInit) {
  if (existingView.type !== nextView.type) return true;

  const existingOptions = existingView.options ?? null;
  const nextOptions = nextView.options ?? null;

  return JSON.stringify(existingOptions) !== JSON.stringify(nextOptions);
}

export const useViewStore = defineStore('view', () => {
  const idStore = useIdStore();

  // Triggers whenever a view is created with a dataID or when a view's dataset changes.
  const ViewDataChangeEvent =
    markRaw(createEventHook<[string, Maybe<string>]>());

  // Triggers whenever a view in the layout is replaced.
  // [beforeViewID, afterViewID]
  const LayoutViewReplacedEvent = markRaw(createEventHook<[string, string]>());

  const parsedDefaultLayouts = parseNamedLayouts(DefaultNamedLayouts);

  const defaultNamedLayoutEntries = Object.entries(parsedDefaultLayouts);
  const firstLayout = defaultNamedLayoutEntries[0][1];
  const firstLayoutName = defaultNamedLayoutEntries[0][0];

  const layout = ref<Layout>(firstLayout.layout);
  // which assigns view IDs to layout slots
  const layoutSlots = ref<string[]>([]);
  const viewByID = reactive<Record<string, ViewInfo>>({});
  const activeView = ref<Maybe<string>>();
  const disabledViewTypes = ref<ViewType[]>([]);
  const namedLayouts =
    ref<Record<string, { layout: Layout; views: ViewInfoInit[] }>>(
      parsedDefaultLayouts
    );
  const currentLayoutName = ref<Maybe<string>>(firstLayoutName);

  const isActiveViewMaximized = ref(false);
  const maximizedView = computed(() => {
    if (activeView.value && isActiveViewMaximized.value)
      return viewByID[activeView.value];
    return undefined;
  });

  const availableViewsForSwitcher = computed(() => {
    const allViews = getAvailableViews();
    return allViews.list.filter(
      (view) => !disabledViewTypes.value.includes(view.type)
    );
  });

  // Every view the current layout assigns a slot to, whether or not it is on
  // screen. Reading the layout rather than `viewByID` keeps views orphaned by a
  // layout switch out of the list.
  const layoutViews = computed(() => {
    const views: ViewInfo[] = [];
    iterLayout(layout.value, (item) => {
      const view = viewByID[layoutSlots.value[item.slotIndex]];
      if (view) views.push(view);
    });
    return views;
  });

  const visibleViews = computed(() => {
    if (maximizedView.value) return [maximizedView.value];
    return layoutViews.value;
  });

  const viewIDs = computed(() => Object.keys(viewByID));

  function getView(id: Maybe<string>) {
    if (!id) return null;
    return viewByID[id] ?? null;
  }

  function getAllViews() {
    return Object.values(viewByID);
  }

  function getViewsForData(dataID: Maybe<string>) {
    if (!dataID) return [];
    return getAllViews().filter((view) => {
      return view.dataID === dataID;
    });
  }

  function setActiveView(id: Maybe<string>) {
    if (activeView.value === id) return;
    activeView.value = id;
  }

  function toggleActiveViewMaximized() {
    if (!activeView.value) return;
    isActiveViewMaximized.value = !isActiveViewMaximized.value;
  }

  function ensureActiveViewIsVisible() {
    const views = visibleViews.value;
    if (!views.length) {
      setActiveView(null);
      return;
    }

    if (!views.find((view) => view.id === activeView.value)) {
      setActiveView(views[0].id);
    }
  }

  function addView(viewInit: ViewInfoInit) {
    const id = idStore.nextId();

    const view = {
      id,
      ...viewInit,
    } satisfies ViewInfo;

    viewByID[view.id] = view;
    if (view.dataID) {
      ViewDataChangeEvent.trigger(view.id, view.dataID);
    }

    return id;
  }

  function replaceView(id: string, viewInfo: ViewInfoInit) {
    const slotIndex = layoutSlots.value.findIndex((viewId) => id === viewId);
    if (slotIndex === -1) throw new Error('invalid view to replace');
    const newViewId = addView(viewInfo);
    layoutSlots.value[slotIndex] = newViewId;
    LayoutViewReplacedEvent.trigger(id, newViewId);

    if (activeView.value === id) {
      setActiveView(newViewId);
    }

    delete viewByID[id];
  }

  function applyLayoutChange(
    newLayout: Layout,
    options?: {
      layoutName?: Maybe<string>;
      updateSlots?: (viewCount: number) => void;
    }
  ) {
    if (options?.layoutName !== undefined) {
      currentLayoutName.value = options.layoutName;
    }

    isActiveViewMaximized.value = false;

    if (options?.updateSlots) {
      const viewCount = calcLayoutViewCount(newLayout);
      options.updateSlots(viewCount);
    }

    layout.value = newLayout;
    ensureActiveViewIsVisible();
  }

  function setLayout(newLayout: Layout) {
    applyLayoutChange(newLayout, {
      updateSlots: (viewCount) => {
        // we don't remove non-visible views so we can preserve their state for later
        while (layoutSlots.value.length < viewCount) {
          layoutSlots.value.push(addView(DEFAULT_VIEW_INIT));
        }
      },
    });
  }

  function setLayoutFromGrid(gridSize: [number, number]) {
    currentLayoutName.value = null;
    setLayout(generateLayoutFromGrid(gridSize));
  }

  function setNamedLayoutsFromConfig(layouts: Record<string, LayoutConfig>) {
    namedLayouts.value = parseNamedLayouts(layouts);
  }

  function switchToNamedLayout(name: string) {
    const namedLayout = namedLayouts.value[name];
    if (!namedLayout) {
      throw new Error(`Named layout "${name}" not found`);
    }
    applyLayoutChange(namedLayout.layout, {
      layoutName: name,
      updateSlots: () => {
        namedLayout.views.forEach((viewInit, index) => {
          if (index < layoutSlots.value.length) {
            const existingViewId = layoutSlots.value[index];
            const existingView = viewByID[existingViewId];
            if (existingView) {
              if (needsViewReplacement(existingView, viewInit)) {
                replaceView(existingViewId, {
                  ...viewInit,
                  dataID: existingView.dataID,
                });
              } else {
                existingView.name = viewInit.name;
              }
            }
          } else {
            layoutSlots.value.push(addView(viewInit));
          }
        });
      },
    });
  }

  function setDataForView(viewID: string, dataID: Maybe<string>) {
    const view = viewByID[viewID];
    if (!view) return;

    view.dataID = dataID;
    ViewDataChangeEvent.trigger(viewID, dataID);
    // If activeView is null/stale, fall back to a visible view so global
    // tools (which resolve their image through activeView) have one to read.
    ensureActiveViewIsVisible();
  }

  function setDataForActiveView(dataID: Maybe<string>) {
    if (!activeView.value) return;
    setDataForView(activeView.value, dataID);
  }

  function setDataForAllViews(dataID: Maybe<string>) {
    viewIDs.value.forEach((viewID) => {
      setDataForView(viewID, dataID);
    });
  }

  function removeDataFromViews(dataID: string) {
    // Every `viewByID` entry is serialized (not just the ones currently in a
    // layout slot), so a view preserved off-slot with a stale dataID would
    // still dangle in the save manifest — unbind ALL matching views.
    getViewsForData(dataID).forEach((view) => {
      setDataForView(view.id, null);
    });
  }

  function applyDisabledViewTypesFilter() {
    if (!disabledViewTypes.value.length) return;

    layoutSlots.value.forEach((id) => {
      const view = viewByID[id];
      if (disabledViewTypes.value.includes(view.type)) {
        const replacement = availableViewsForSwitcher.value[0];
        if (replacement) {
          replaceView(id, replacement);
        }
      }
    });
  }

  function serialize(stateFile: StateFile) {
    const { manifest } = stateFile;
    manifest.layout = layout.value;
    manifest.activeView = activeView.value;
    manifest.isActiveViewMaximized = isActiveViewMaximized.value;
    manifest.layoutSlots = layoutSlots.value;
    manifest.viewByID = viewByID;
  }

  function deserializeLayout(manifest: Manifest) {
    if (manifest.layout) {
      setLayout(manifest.layout);
    }
    setActiveView(manifest.activeView);
    if (manifest.isActiveViewMaximized !== undefined) {
      isActiveViewMaximized.value = manifest.isActiveViewMaximized;
    }
    if (manifest.layoutSlots) {
      layoutSlots.value = manifest.layoutSlots;
    }

    if (manifest.viewByID) {
      viewIDs.value.forEach((key) => {
        delete viewByID[key];
      });

      Object.entries(manifest.viewByID).forEach(([id, view]) => {
        viewByID[id] = {
          ...view,
          dataID: null,
        } as unknown as ViewInfo;
      });
    }
  }

  function bindViewsToData(
    stateID: string,
    storeID: string,
    manifest: Manifest
  ) {
    if (!manifest.viewByID) return;

    Object.entries(manifest.viewByID).forEach(([id, view]) => {
      if (view.dataID === stateID && viewByID[id]) {
        setDataForView(id, storeID);
      }
    });
  }

  // initialization

  firstLayout.views.forEach((viewInit) => {
    const viewId = addView(viewInit);
    layoutSlots.value.push(viewId);
    if (!activeView.value) {
      setActiveView(viewId);
    }
  });

  watch(disabledViewTypes, () => {
    applyDisabledViewTypesFilter();
  });

  onImageDeleted((deletedIDs) => {
    deletedIDs.forEach((id) => removeDataFromViews(id));
  });

  return {
    visibleLayout: computed<Layout>(() => {
      if (maximizedView.value)
        return {
          direction: 'column',
          items: [{ type: 'slot', slotIndex: 0 }],
        } satisfies Layout;
      return layout.value;
    }),
    visibleViews,
    layoutViews,
    viewIDs,
    activeView,
    viewByID,
    disabledViewTypes,
    availableViewsForSwitcher,
    namedLayouts,
    currentLayoutName,
    getView,
    getAllViews,
    getViewsForData,
    replaceView,
    setLayout,
    setLayoutFromGrid,
    setNamedLayoutsFromConfig,
    switchToNamedLayout,
    setActiveView,
    setDataForView,
    setDataForActiveView,
    setDataForAllViews,
    removeDataFromViews,
    toggleActiveViewMaximized,
    serialize,
    deserializeLayout,
    bindViewsToData,
    ViewDataChangeEvent,
    LayoutViewReplacedEvent,
  };
});
